// Phase 123 Plan 06 Task 1 — Frontend user-identity propagation.
//
// useSession() exposes the authenticated user reactively. When the user
// becomes known (login / session refresh via /auth/me), we pin them on the
// Sentry scope via setUser so every subsequent event + breadcrumb in the
// browser session carries the same identity. On logout / user-cleared, we
// call setUser(null) to scrub the identity.
//
// RESEARCH §Pattern 5 (frontend half). Mounted once at the App tree root
// via <SentrySessionBinding />.
import { useEffect } from 'react';
import { useSession } from '@/contexts/session-context';
import { setUser } from '@/lib/sentry';

/**
 * React hook — call once near the top of the app tree. Idempotent: re-runs
 * whenever the user id or email changes (including sign-out where the user
 * object becomes null).
 */
export function useSentrySessionBinding(): void {
  const { user } = useSession();
  useEffect(() => {
    if (user?.id) {
      setUser({ id: user.id, email: user.email ?? undefined });
    } else {
      setUser(null);
    }
  }, [user?.id, user?.email]);
}

/**
 * Zero-DOM component wrapper — lets the binding live inside the SessionProvider
 * tree without a dedicated top-level useEffect in App().
 */
export function SentrySessionBinding(): null {
  useSentrySessionBinding();
  return null;
}
